import { supabase } from '../lib/supabase'

export interface CompanyLine {
  id: string
  code: 'office' | 'field'
  label: string
  phone_number: string | null
  whatsapp_enabled: boolean
  sms_enabled: boolean
}

export interface ExternalThread {
  id: string
  company_line_id: string
  scope_type: 'customer' | 'job'
  customer_id: string | null
  job_id: string | null
  title: string | null
  status: string
}

export async function fetchCompanyLines(): Promise<CompanyLine[]> {
  const { data, error } = await supabase
    .from('company_lines')
    .select('id, code, label, phone_number, whatsapp_enabled, sms_enabled')
    .eq('is_active', true)
    .order('code')
  if (error) throw error
  return (data || []) as CompanyLine[]
}

export async function createFieldThread(input: {
  company_line_id: string
  scope_type: 'customer' | 'job'
  customer_id?: string | null
  job_id?: string | null
  title?: string
}): Promise<string> {
  const { data: session } = await supabase.auth.getUser()
  const { data, error } = await supabase
    .from('external_threads')
    .insert({
      company_line_id: input.company_line_id,
      scope_type: input.scope_type,
      customer_id: input.customer_id || null,
      job_id: input.job_id || null,
      title: input.title || null,
      created_by: session.user?.id || null,
      status: 'active',
    })
    .select('id')
    .single()
  if (error) throw error
  return data.id
}

/** Patch an internal user into a field-line thread (join-forward visibility). */
export async function patchInternalMember(input: {
  thread_id: string
  user_id: string
  employee_id?: string | null
  is_permanent?: boolean
}): Promise<void> {
  const { error } = await supabase.from('external_thread_members').insert({
    thread_id: input.thread_id,
    member_kind: 'internal',
    user_id: input.user_id,
    employee_id: input.employee_id || null,
    is_permanent: !!input.is_permanent,
    joined_at: new Date().toISOString(),
  })
  if (error) throw error
}

export async function addExternalMember(input: {
  thread_id: string
  contact_name: string
  contact_phone?: string | null
  contact_email?: string | null
  channel: 'whatsapp' | 'sms' | 'email'
  conversation_id?: string | null
}): Promise<void> {
  const { error } = await supabase.from('external_thread_members').insert({
    thread_id: input.thread_id,
    member_kind: 'external',
    contact_name: input.contact_name,
    contact_phone: input.contact_phone || null,
    contact_email: input.contact_email || null,
    channel: input.channel,
    conversation_id: input.conversation_id || null,
    is_permanent: true,
  })
  if (error) throw error
}

export async function endInternalMembership(memberId: string): Promise<void> {
  const { error } = await supabase
    .from('external_thread_members')
    .update({ left_at: new Date().toISOString() })
    .eq('id', memberId)
  if (error) throw error
}

export async function fetchThreadMessages(threadId: string) {
  const { data, error } = await supabase
    .from('external_thread_messages')
    .select('*')
    .eq('thread_id', threadId)
    .order('created_at', { ascending: true })
  if (error) throw error
  return data || []
}

export async function postThreadMessage(threadId: string, body: string, senderMemberId?: string) {
  const { error } = await supabase.from('external_thread_messages').insert({
    thread_id: threadId,
    sender_member_id: senderMemberId || null,
    body: body.trim(),
    delivery_status: 'pending',
    visible_from: new Date().toISOString(),
  })
  if (error) throw error
}
